/**
 * OutOfHoursConfigSection - Auto-resposta fora do horário de atendimento
 *
 * Envia uma mensagem automática ao contato quando ele escreve fora do
 * horário comercial configurado na empresa (Configurações → Empresa →
 * Horário de atendimento). Ausente ou enabled !== true = desligado
 * (day-0 off). Só atua quando não há agente de IA ativo na conversa.
 */

import React from 'react';
import type { UpdateChannelRequest, ChannelOutOfHoursConfig } from '@troia-v3/types';
import type { ChannelResponse } from '@/modules/channels/types';
import { Card, CardHeader, CardContent } from '@/shared/components/ui/Card';
import { Switch } from '@/shared/components/ui/Switch';
import { Textarea } from '@/shared/components/ui/Textarea';
import { Icon } from '@/shared/icons';

interface OutOfHoursConfigSectionProps {
  channel: ChannelResponse;
  formData: Partial<UpdateChannelRequest>;
  onFieldChange: (field: keyof UpdateChannelRequest, value: any) => void;
}

const DEFAULT_MESSAGE =
  'Estamos fora do horário de atendimento. Retornaremos sua mensagem no próximo horário comercial.';

const DEFAULT_CONFIG: ChannelOutOfHoursConfig = {
  enabled: false,
  message: DEFAULT_MESSAGE,
};

export const OutOfHoursConfigSection: React.FC<OutOfHoursConfigSectionProps> = ({
  channel,
  formData,
  onFieldChange,
}) => {
  const currentConfig: ChannelOutOfHoursConfig =
    formData.outOfHoursConfig || channel.outOfHoursConfig || DEFAULT_CONFIG;

  const isEnabled = currentConfig.enabled;

  // Sempre envia o objeto completo — o PUT genérico substitui o sub-objeto inteiro
  const emit = (partial: Partial<ChannelOutOfHoursConfig>) => {
    onFieldChange('outOfHoursConfig', { ...currentConfig, ...partial });
  };

  const handleToggle = (checked: boolean) => {
    emit({ enabled: checked });
  };

  const handleMessageChange = (value: string) => {
    emit({ message: value });
  };

  return (
    <Card data-testid="out-of-hours-config-section">
      <CardHeader className="pb-3 px-4 py-3">
        <div className="flex items-center gap-2">
          <Icon name="moon" className="w-4 h-4" style={{ color: 'var(--theme-button-primary)' }} />
          <h3 className="font-bold text-[15px]" style={{ color: 'var(--theme-card-text)' }}>
            Fora do horário de atendimento
          </h3>
        </div>
      </CardHeader>

      <CardContent className="px-4 pb-4 space-y-4">
        {/* Toggle de habilitação */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <label
              htmlFor="out-of-hours-toggle"
              className="block text-sm font-medium mb-1"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Responder automaticamente fora do horário
            </label>
            <p className="text-xs" style={{ color: 'var(--theme-card-muted)' }}>
              Envia uma mensagem automática quando o cliente escreve fora do horário comercial
            </p>
          </div>
          <Switch
            id="out-of-hours-toggle"
            checked={isEnabled}
            onChange={handleToggle}
            data-testid="out-of-hours-toggle"
          />
        </div>

        {/* Configurações quando habilitado */}
        {isEnabled && (
          <div className="space-y-4 pt-4 border-t" style={{ borderColor: 'var(--theme-card-border)' }}>
            {/* Mensagem */}
            <div>
              <label
                htmlFor="out-of-hours-message"
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Mensagem automática
              </label>
              <Textarea
                id="out-of-hours-message"
                value={currentConfig.message}
                onChange={(e) => handleMessageChange(e.target.value)}
                placeholder="Ex: Estamos fora do horário de atendimento. Retornaremos sua mensagem no próximo horário comercial."
                maxLength={1000}
                rows={3}
                data-testid="out-of-hours-message"
              />
            </div>

            {/* Info box */}
            <div
              className="p-3 rounded-lg border"
              style={{
                backgroundColor: 'var(--theme-card-background)',
                borderColor: 'var(--theme-card-border)',
              }}
            >
              <div className="flex items-start gap-2">
                <Icon
                  name="info"
                  className="w-4 h-4 mt-0.5 flex-shrink-0"
                  style={{ color: 'var(--theme-card-muted)' }}
                />
                <p className="text-xs" style={{ color: 'var(--theme-card-muted)' }}>
                  O horário comercial é o configurado em Configurações → Empresa → Horário de
                  atendimento. Mensagens seguidas do cliente recebem um aviso só: ele sai no máximo
                  uma vez por hora e 3 vezes por conversa, e apenas quando não há agente de IA ativo.
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

OutOfHoursConfigSection.displayName = 'OutOfHoursConfigSection';
